import { Box, Typography } from "@mui/material";

import useMaintenance from "features/maintenance/hooks/useMaintenance";
import { useEffect, useState } from "react";
import MaintenanceForm from "features/maintenance/components/MaintenanceForm";
import DeleteConfirmationDialog from "features/maintenance/components/DeleteConfirmationDialog";
import { useNotification } from "context/NotificationContext";
import validateForm from "features/maintenance/utils/validateForm";
import MaintenanceToolbar from "features/maintenance/components/MaintenanceToolbar";
import GridContainer from "components/common/GridContainer";
import GridItem from "components/common/GridItem";
import GridForm from "features/maintenance/pages/MaintenancePage/components/GridForm";
import FreeForm from "features/maintenance/pages/MaintenancePage/components/FreeForm";
import { AssignmentOutlined } from "@mui/icons-material";

export default function MaintenancePage({ resource }) {
    const {
        items,
        filteredItems,
        keyword,
        setKeyword,
        loading,
        refresh,
        error,
        search
    } = useMaintenance(resource);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [form, setForm] = useState({});
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [showValidation, setShowValidation] = useState(false);
    const [isUpdating, setUpdating] = useState(false);
    const EditableForm = resource?.editableForm ?? MaintenanceForm;

    const notify = useNotification();

    useEffect(() => {
        search();
    }, []);

    useEffect(() => {
        if (error) {
            console.error(error, "error fetching data:");
            try {
                notify.error("Fetch data error: " + error?.message, "Error");
            } catch (e) {
                console.error(e);
            }
        }
    }, [error]);

    useEffect(() => {
        if (resource?.freeForm) {
            console.log("resource.freeForm", items);
            if (items) {
                if (Array.isArray(items) && items?.length > 0) {
                    setForm(items[0]);
                    setEditingItem(items[0]);
                } else if (typeof items === "object") {
                    setForm(items);
                    setEditingItem(items);
                }
            }
        }
    }, [resource?.freeForm, items]);

    const initializeDialog = () => {
        setEditingItem(null);
        // setForm({});
        setShowValidation(false);
        setDialogOpen(false);
        setDeleteDialogOpen(false);
        setItemToDelete(null);
    };

    const hasChanged = () => {
        return (
            !!form &&
            /*(!!editingItem ) &&*/
            JSON.stringify(form) !== JSON.stringify(editingItem || {})
        );
    };
    const hasErrors = () => {
        const errors = validateForm(resource.fields, form, items);

        return Object.keys(errors).length > 0;
    };

    const handleSave = async () => {
        setShowValidation(true);

        if (hasErrors()) {
            console.error("Form has errors");
            return;
        }

        try {
            setUpdating(true);
            if (editingItem) {
                await resource.service.update(editingItem.id, form);
            } else {
                await resource.service.create(form);
            }

            await refresh();

            notify.success(
                editingItem
                    ? `${resource.singular} updated successfully.`
                    : `${resource.singular} created successfully.`
            );
            setUpdating(false);
            initializeDialog();
        } catch (error) {
            setUpdating(false);
            let message =
                error?.response?.data?.errors || error?.response?.data?.message;
            if (message && typeof message === "object") {
                message = Object.values(message).join(", ");
            }
            console.error({ error, message });
            notify.error(
                "Save failed with error: " + error?.message + " " + message,
                "Error"
            );
        }
    };

    const handleDelete = async () => {
        try {
            await resource.service.delete(itemToDelete.id);

            await refresh();

            notify.success(`${resource.singular} deleted successfully.`);

            initializeDialog();
        } catch (error) {
            console.error(error);
        }
    };

    const handleNew = () => {
        initializeDialog();
        setForm({});
        setEditingItem(null);

        /* setTimeout(() => {
             setDialogOpen(true);
         }, 300);*/
        setDialogOpen(true);
    };

    const handleEdit = (row) => {
        initializeDialog();
        setForm(row);
        setEditingItem(row);
        setDialogOpen(true);
    };

    const onDelete = (row) => {
        initializeDialog();
        setItemToDelete(row);
        setDeleteDialogOpen(true);
    };

    const onCancel = () => {
        setDialogOpen(false);
        initializeDialog();
    };

    const PageIcon = resource?.pageIcon || AssignmentOutlined;

    const formProps = {
        resource,
        form,
        setForm,
        showValidation,
        items,
        filteredItems,
        handleEdit,
        onDelete,
        onCancel,
        handleSave,
        hasErrors,
        editableForm: EditableForm,
        dialogOpen,
        canSave: !hasErrors() && hasChanged(),
        pageIcon: <PageIcon />,
        loading: isUpdating || loading
    };

    return (
        <Box>
            <GridContainer>
                <GridItem sx={{ flex: 1 }}>
                    <GridContainer alignItems="center">
                        <GridItem>
                            <div style={{ position: "relative", top: "8px" }}>
                                <PageIcon
                                    sx={{
                                        fill: "#999594 !important"
                                        // fontSize: "40px"
                                    }}
                                />
                            </div>
                        </GridItem>
                        <GridItem>
                            <Typography
                                variant="h4"
                                sx={{
                                    mb: 3,
                                    textTransform: "uppercase",
                                    fontWeight: "bold"
                                }}
                            >
                                {resource.title}
                            </Typography>
                        </GridItem>
                    </GridContainer>
                </GridItem>
                {!resource?.freeForm && (
                    <GridItem>
                        <MaintenanceToolbar
                            keyword={keyword}
                            onKeywordChange={setKeyword}
                            onNew={handleNew}
                        />
                    </GridItem>
                )}
            </GridContainer>

            {resource?.freeForm ? (
                <FreeForm {...formProps} />
            ) : (
                <GridForm {...formProps} />
            )}

            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                title={`Delete ${resource.singular}`}
                message={`Are you sure you want to delete "${itemToDelete?.[resource.displayField]}"?`}
                onCancel={() => {
                    setDeleteDialogOpen(false);
                    setItemToDelete(null);
                }}
                onConfirm={handleDelete}
                loading={loading || isUpdating}
            />

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {items.length} record{items.length !== 1 ? "s" : ""}
            </Typography>
        </Box>
    );
}
